import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Info, Image as ImageIcon, Type, Stamp, Loader2, Star } from "lucide-react";
import GeneratedPageContent from "../GeneratedPageContent";
import { ImagePickerDialog } from "./ImagePickerDialog";
import { LogoDialog } from "./LogoDialog";
import { SocialProofDialog } from "./SocialProofDialog";
import { GoogleReviewsDialog } from "./GoogleReviewsDialog";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BLOCKED = new Set(["images", "hero_image", "section_image", "trust_image", "industry_category", "structure", "seo_title", "seo_description", "seo_keywords"]);

function collectTexts(content) {
  const map = new Map();
  const add = (val, path) => {
    const v = typeof val === "string" ? val.trim() : "";
    if (v.length >= 3 && !v.startsWith("http") && !map.has(v)) map.set(v, path);
  };
  Object.entries(content || {}).forEach(([key, val]) => {
    if (BLOCKED.has(key)) return;
    if (typeof val === "string") add(val, key);
    else if (Array.isArray(val)) val.forEach((item, i) => {
      if (typeof item === "string") add(item, `${key}[${i}]`);
      else if (item && typeof item === "object") Object.entries(item).forEach(([sub, sv]) => /^[a-z_]+$/.test(sub) && add(sv, `${key}[${i}].${sub}`));
    });
  });
  return map;
}

export function WebsiteEditor({ page, pageId, onChanged }) {
  const contentRef = useRef(null);
  const [mode, setMode] = useState("images");
  const [badges, setBadges] = useState([]);
  const [activeSlot, setActiveSlot] = useState(null);
  const [logoOpen, setLogoOpen] = useState(false);
  const [proofOpen, setProofOpen] = useState(false);
  const [googleOpen, setGoogleOpen] = useState(false);
  const [textEdit, setTextEdit] = useState(null);
  const [saving, setSaving] = useState(false);
  const textMap = useMemo(() => collectTexts(page?.content), [page]);

  const measure = useCallback(() => {
    const root = contentRef.current;
    if (!root) return;
    const base = root.getBoundingClientRect();
    const next = mode !== "images" ? [] : [...root.querySelectorAll("img[data-img-slot]")].map((img, i) => {
      const r = img.getBoundingClientRect();
      return { key: `${img.dataset.imgSlot}-${i}`, slot: img.dataset.imgSlot, top: r.top - base.top + 10, left: r.left - base.left + Math.max(10, r.width - 150), visible: r.width > 120 && r.height > 80 };
    });
    setBadges(prev => (JSON.stringify(prev) === JSON.stringify(next) ? prev : next));
    root.querySelectorAll(".wg-editable").forEach(el => { el.classList.remove("wg-editable"); delete el.dataset.wgPath; });
    if (mode === "texts") {
      root.querySelectorAll("h1,h2,h3,h4,h5,p,span,li,a,button,div").forEach(el => {
        if (el.children.length > 0) return;
        const path = textMap.get((el.textContent || "").trim());
        if (path) { el.classList.add("wg-editable"); el.dataset.wgPath = path; }
      });
    }
  }, [mode, textMap]);

  useEffect(() => {
    measure();
    const id = setInterval(measure, 700);
    window.addEventListener("resize", measure);
    return () => { clearInterval(id); window.removeEventListener("resize", measure); };
  }, [measure, page]);

  const onClickCapture = (e) => {
    if (mode !== "texts") return;
    const el = e.target.closest?.("[data-wg-path]");
    if (!el) return;
    e.preventDefault();
    e.stopPropagation();
    setTextEdit({ path: el.dataset.wgPath, value: (el.textContent || "").trim() });
  };

  const saveText = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/pages/${pageId}/texts`, { path: textEdit.path, value: textEdit.value }, { headers: { Authorization: `Bearer ${localStorage.getItem("auth_token")}` } });
      toast.success("Text gespeichert");
      setTextEdit(null);
      onChanged();
    } catch (err) { toast.error(err.response?.data?.detail || "Text konnte nicht gespeichert werden"); }
    finally { setSaving(false); }
  };

  const ModeBtn = ({ id, icon: Icon, label }) => (
    <button onClick={() => setMode(id)} data-testid={`editor-mode-${id}`}
      className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${mode === id ? "bg-white text-indigo-700 shadow-sm" : "text-slate-600 hover:text-slate-900"}`}>
      <Icon className="w-4 h-4" />{label}
    </button>
  );

  return (
    <div data-testid="website-editor">
      <style>{".wg-editable{outline:2px dashed #6366f1;outline-offset:3px;cursor:text;border-radius:4px}.wg-editable:hover{background:rgba(99,102,241,.14)}"}</style>
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1">
          <ModeBtn id="images" icon={ImageIcon} label="Bilder ändern" />
          <ModeBtn id="texts" icon={Type} label="Texte bearbeiten" />
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" onClick={() => setProofOpen(true)} className="rounded-full" data-testid="social-proof-open-btn">
            <Type className="w-4 h-4 mr-2" />Kundenstimmen &amp; Kennzahlen
          </Button>
          <Button variant="outline" onClick={() => setGoogleOpen(true)} className="rounded-full" data-testid="google-reviews-open-btn">
            <Star className="w-4 h-4 mr-2" />{page?.content?.google_reviews ? "Google-Bewertungen" : "Google-Bewertungen verknüpfen"}
          </Button>
          <Button variant="outline" onClick={() => setLogoOpen(true)} className="rounded-full" data-testid="logo-open-btn">
            <Stamp className="w-4 h-4 mr-2" />{page?.logo_url ? "Logo ändern" : "Logo hinzufügen"}
          </Button>
        </div>
      </div>
      <div className="flex items-start gap-3 bg-indigo-50 border border-indigo-100 rounded-xl px-4 py-3 mb-4 text-sm text-indigo-800" data-testid="editor-hint">
        <Info className="w-4 h-4 mt-0.5 flex-shrink-0" />
        <p>{mode === "images" ? <>Fahre mit der Maus über ein Bild und klicke auf <strong>„Bild ändern“</strong>.</> : <>Klicke auf einen <strong>gestrichelt markierten Text</strong>, um ihn zu ändern. Links und Buttons sind in diesem Modus nur zum Bearbeiten klickbar.</>}</p>
      </div>
      <div className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="h-9 bg-slate-100 border-b border-slate-200 flex items-center gap-1.5 px-4">
          <span className="w-2.5 h-2.5 rounded-full bg-slate-300" /><span className="w-2.5 h-2.5 rounded-full bg-slate-300" /><span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
          <span className="ml-3 text-xs text-slate-500 truncate">{page?.custom_domain || (page?.subdomain ? `${page.subdomain}.webgenerator.io` : "Vorschau")}</span>
        </div>
        <div className="h-[72vh] overflow-y-auto" data-testid="website-editor-frame">
          <div ref={contentRef} className="relative" onLoadCapture={measure} onClickCapture={onClickCapture}>
            <GeneratedPageContent page={page} isPreview={true} />
            {badges.filter(b => b.visible).map((b) => (
              <button key={b.key} onClick={() => setActiveSlot(b.slot)} data-testid={`image-edit-${b.key}`}
                style={{ position: "absolute", top: b.top, left: b.left, zIndex: 40 }}
                className="flex items-center gap-1.5 bg-white/95 hover:bg-indigo-600 hover:text-white text-slate-800 text-xs font-semibold px-3 py-1.5 rounded-full shadow-lg border border-slate-200 transition-colors">
                <Pencil className="w-3.5 h-3.5" /> Bild ändern
              </button>
            ))}
          </div>
        </div>
      </div>
      <ImagePickerDialog open={!!activeSlot} slot={activeSlot} pageId={pageId} onClose={() => setActiveSlot(null)} onChanged={onChanged} />
      <GoogleReviewsDialog open={googleOpen} onClose={() => setGoogleOpen(false)} pageId={pageId} page={page} onChanged={onChanged} />
      <SocialProofDialog open={proofOpen} onClose={() => setProofOpen(false)} pageId={pageId} content={page?.content} onChanged={onChanged} />
      <LogoDialog open={logoOpen} onClose={() => setLogoOpen(false)} pageId={pageId} logoUrl={page?.logo_url} onChanged={onChanged} />
      <Dialog open={!!textEdit} onOpenChange={(v) => !v && setTextEdit(null)}>
        <DialogContent className="max-w-lg rounded-2xl" data-testid="text-edit-dialog">
          <DialogHeader>
            <DialogTitle>Text bearbeiten</DialogTitle>
            <DialogDescription>Die Änderung wird sofort auf deiner Webseite sichtbar.</DialogDescription>
          </DialogHeader>
          <textarea value={textEdit?.value || ""} onChange={(e) => setTextEdit(t => ({ ...t, value: e.target.value }))} rows={5} maxLength={2000}
            className="w-full rounded-xl border border-slate-200 p-3 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500" data-testid="text-edit-input" />
          <div className="flex justify-end gap-2">
            <Button variant="ghost" onClick={() => setTextEdit(null)} data-testid="text-edit-cancel">Abbrechen</Button>
            <Button onClick={saveText} disabled={saving || !textEdit?.value?.trim()} className="rounded-full bg-indigo-600 hover:bg-indigo-700" data-testid="text-edit-save">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Speichern"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
